/**
 * =====================================================
 * NAHHAR FASHIONS — JavaScript System
 * asad-system.js
 * Single ES module. Carousel + Scroll Reveal + Video
 * =====================================================
 */

// ── Scroll Reveal (IntersectionObserver) ─────────────

function initScrollReveal() {
  const elements = document.querySelectorAll('.asad-reveal');
  if (!elements.length) {
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('asad-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    {
      threshold: 0.12,
      rootMargin: '0px 0px -40px 0px',
    }
  );

  elements.forEach((el) => observer.observe(el));
}

// ── Carousel Engine ───────────────────────────────────

class AsadCarousel {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('.asad-carousel__track');
    this.slides = el.querySelectorAll('.asad-carousel__slide');
    this.dots = el.querySelectorAll('.asad-carousel__dot');
    this.btnPrev = el.querySelector('.asad-carousel__nav--prev');
    this.btnNext = el.querySelector('.asad-carousel__nav--next');

    if (!this.track || !this.slides.length) {
      return;
    }

    this.currentIndex = 0;
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.dragOffset = 0;
    this.autoplayTimer = null;
    this.autoplayInterval = 0;
    this.perView = this._getPerView();

    this._bind();
    this._updateNavState();

    // Autoplay (optional, data attribute)
    const autoplay = parseInt(this.el.dataset.autoplay, 10);
    if (autoplay > 0) {
      this.autoplayInterval = autoplay;
      this._startAutoplay(autoplay);
    }
  }

  _getPerView() {
    const w = window.innerWidth;
    const mobile = parseInt(this.el.dataset.perViewMobile, 10) || 1;
    const tablet = parseInt(this.el.dataset.perViewTablet, 10) || 2;
    const desktop = parseInt(this.el.dataset.perView, 10) || 3;
    if (w < 640) {
      return mobile;
    }
    if (w < 1024) {
      return tablet;
    }
    return desktop;
  }

  _slideWidth() {
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    const trackW = this.track.parentElement.offsetWidth;
    return (trackW - gap * (this.perView - 1)) / this.perView;
  }

  _maxIndex() {
    return Math.max(0, this.slides.length - this.perView);
  }

  _translateTo(index, animated = true) {
    this.currentIndex = Math.max(0, Math.min(index, this._maxIndex()));
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    const offset = this.currentIndex * (this._slideWidth() + gap);
    this.track.style.transition = animated ? '' : 'none';
    this.track.style.transform = `translateX(-${offset}px)`;
    this._updateDots();
    this._updateNavState();
  }

  _updateNavState() {
    if (this.btnPrev) {
      this.btnPrev.disabled = this.currentIndex === 0;
    }
    if (this.btnNext) {
      this.btnNext.disabled = this.currentIndex >= this._maxIndex();
    }
  }

  _updateDots() {
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.currentIndex);
    });
  }

  _startAutoplay(interval) {
    this._stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      const next = this.currentIndex >= this._maxIndex() ? 0 : this.currentIndex + 1;
      this._translateTo(next);
    }, interval);
  }

  _stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  _pauseAutoplay() {
    this._stopAutoplay();
  }

  _resumeAutoplay() {
    if (this.autoplayInterval > 0) {
      this._startAutoplay(this.autoplayInterval);
    }
  }

  // Touch / mouse drag
  _onPointerDown(e) {
    this.isDragging = true;
    this.startX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    this.dragOffset = 0;
    this.track.classList.add('is-dragging');
    this._pauseAutoplay();
    e.preventDefault();
  }

  _onPointerMove(e) {
    if (!this.isDragging) {
      return;
    }
    const x = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    this.dragOffset = x - this.startX;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    const base = this.currentIndex * (this._slideWidth() + gap);
    this.track.style.transition = 'none';
    this.track.style.transform = `translateX(${-base + this.dragOffset}px)`;
  }

  _onPointerUp() {
    if (!this.isDragging) {
      return;
    }
    this.isDragging = false;
    this.track.classList.remove('is-dragging');
    this.track.style.transition = '';
    const threshold = this._slideWidth() * 0.25;
    if (this.dragOffset < -threshold) {
      this._translateTo(this.currentIndex + 1);
    } else if (this.dragOffset > threshold) {
      this._translateTo(this.currentIndex - 1);
    } else {
      this._translateTo(this.currentIndex);
    }
    // Resume autoplay after manual interaction
    this._resumeAutoplay();
  }

  _bind() {
    if (this.btnPrev) {
      this.btnPrev.addEventListener('click', () => {
        this._translateTo(this.currentIndex - 1);
        this._resumeAutoplay();
      });
    }
    if (this.btnNext) {
      this.btnNext.addEventListener('click', () => {
        this._translateTo(this.currentIndex + 1);
        this._resumeAutoplay();
      });
    }

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this._translateTo(i);
        this._resumeAutoplay();
      });
    });

    // Touch
    this.track.addEventListener('touchstart', (e) => this._onPointerDown(e), { passive: false });
    this.track.addEventListener('touchmove', (e) => this._onPointerMove(e), { passive: false });
    this.track.addEventListener('touchend', () => this._onPointerUp());

    // Mouse drag
    this.track.addEventListener('mousedown', (e) => this._onPointerDown(e));
    window.addEventListener('mousemove', (e) => this._onPointerMove(e));
    window.addEventListener('mouseup', () => this._onPointerUp());

    // Pause on hover, resume on leave
    this.el.addEventListener('mouseenter', () => this._pauseAutoplay());
    this.el.addEventListener('mouseleave', () => this._resumeAutoplay());

    // Pause when tab is hidden, resume when visible
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this._pauseAutoplay();
      } else {
        this._resumeAutoplay();
      }
    });

    // Resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.perView = this._getPerView();
        this._translateTo(this.currentIndex, false);
      }, 150);
    });
  }
}

function initCarousels() {
  document.querySelectorAll('.asad-carousel').forEach((el) => {
    new AsadCarousel(el);
  });
}

// ── Video Lazy Loader ─────────────────────────────────

function initVideoLazyLoad() {
  const videos = document.querySelectorAll('video[data-asad-lazy]');
  if (!videos.length) {
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }
        const video = entry.target;
        Array.from(video.children).forEach((source) => {
          if (source.dataset.src) {
            source.src = source.dataset.src;
          }
        });
        video.load();
        video.play().catch(() => {
          // Autoplay blocked — fine, the poster image remains
        });
        observer.unobserve(video);
      });
    },
    { threshold: 0.1 }
  );

  videos.forEach((video) => observer.observe(video));
}

// ── Hero Parallax (subtle) ───────────────────────────

function initHeroParallax() {
  const hero = document.querySelector('.asad-hero__media');
  if (!hero) {
    return;
  }

  // Only on devices that can handle it (not mobile)
  if (window.matchMedia('(max-width: 768px)').matches) {
    return;
  }

  let ticking = false;
  window.addEventListener('scroll', () => {
    if (ticking) {
      return;
    }
    window.requestAnimationFrame(() => {
      const scrolled = window.scrollY;
      hero.style.transform = `translateY(${scrolled * 0.25}px)`;
      ticking = false;
    });
    ticking = true;
  }, { passive: true });
}

// ── Add-to-Cart Micro-animation ───────────────────────

function initCartAnimation() {
  // Listens for Horizon's native cart add events
  document.addEventListener('product-form:submit', () => {
    // Nothing to do — Horizon handles fly-to-cart
    // This hook is for future custom ATC animation
  });
}

// ── Section Entry Stagger ─────────────────────────────

function initSectionStagger() {
  const groups = document.querySelectorAll('.asad-reveal-group');
  groups.forEach((group) => {
    const children = Array.from(group.children);
    children.forEach((child) => {
      child.classList.add('asad-reveal', 'asad-reveal--fade-up');
    });
  });
}

// ── Announce Bar scroll link ──────────────────────────

function initScrollArrow() {
  const arrow = document.querySelector('.asad-hero__scroll-arrow');
  if (!arrow) {
    return;
  }
  arrow.addEventListener('click', () => {
    const next = document.querySelector('#MainContent > * + *');
    if (next) {
      next.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
}

// ── Sticky Header enhancement ─────────────────────────

function initStickyHeader() {
  const header = document.querySelector('#header-group');
  if (!header) {
    return;
  }
  let lastScroll = 0;
  window.addEventListener('scroll', () => {
    const current = window.scrollY;
    if (current > 80) {
      header.classList.add('asad-header--scrolled');
    } else {
      header.classList.remove('asad-header--scrolled');
    }
    lastScroll = current;
  }, { passive: true });
}

// ── Bootstrap ─────────────────────────────────────────

function init() {
  initSectionStagger();
  initScrollReveal();
  initCarousels();
  initVideoLazyLoad();
  initHeroParallax();
  initCartAnimation();
  initScrollArrow();
  initStickyHeader();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Re-init after Shopify section renders in editor
document.addEventListener('shopify:section:load', () => {
  initScrollReveal();
  initCarousels();
  initVideoLazyLoad();
});
